import React, { useEffect, useRef, useState } from 'react';
import { FocusedField } from './ApplicationSession';
export var useCreditCardField = function (formRef, applicationSession, cardFieldStyling, locale, onInitialized) {
    var _a = useState(undefined), creditCardPayload = _a[0], setCreditCardPayload = _a[1];
    var _b = useState(undefined), selectedCardBrand = _b[0], setSelectedCardBrand = _b[1];
    var _c = useState(false), showCardNumberContents = _c[0], setShowCardNumberContents = _c[1];
    var _d = useState(true), cardNumberEmpty = _d[0], setCardNumberEmpty = _d[1];
    var _e = useState([]), supportedCardBrands = _e[0], setSupportedCardBrands = _e[1];
    var _f = useState(false), showSecurityCodeContents = _f[0], setShowSecurityCodeContents = _f[1];
    var _g = useState(true), securityCodeEmpty = _g[0], setSecurityCodeEmpty = _g[1];
    var _h = useState(false), showExpiryDateContents = _h[0], setShowExpiryDateContents = _h[1];
    var _j = useState(true), expiryDateEmpty = _j[0], setExpiryDateEmpty = _j[1];
    var _k = useState(undefined), cardValidityState = _k[0], setCardValidityState = _k[1];
    var localeFirstRender = useRef(true);
    useEffect(function () {
        if (applicationSession === undefined || formRef.current === null) {
            return;
        }
        var callbacks = {
            onBrand: setSelectedCardBrand,
            onCreditCardFormValidated: function (creditCardFormFields) {
                setCreditCardPayload(creditCardFormFields);
            },
            onValidityChange: function (validityState) {
                setCardValidityState(validityState);
                if (!validityState.isValid) {
                    setCreditCardPayload(undefined);
                }
            },
            onFocusChange: function (field, inFocus, isEmpty) {
                switch (field) {
                    case FocusedField.CARD_NUMBER:
                        setShowCardNumberContents(inFocus);
                        setCardNumberEmpty(isEmpty);
                        break;
                    case FocusedField.SECURITY_CODE:
                        setShowSecurityCodeContents(inFocus);
                        setSecurityCodeEmpty(isEmpty);
                        break;
                    case FocusedField.EXPIRY_DATE:
                        setShowExpiryDateContents(inFocus);
                        setExpiryDateEmpty(isEmpty);
                        break;
                }
            }
        };
        setSupportedCardBrands(applicationSession.getSupportedCardBrands());
        applicationSession.initializeCreditCard(formRef, callbacks, cardFieldStyling, applicationSession.getSupportedCardBrands(), onInitialized);
    }, [formRef, applicationSession]);
    // remount when locale is changed to fix issue of uneditable CreditCardFormFields
    useEffect(function () {
        // Only remount for succeeding changes, not initially
        if (!localeFirstRender.current) {
            applicationSession === null || applicationSession === void 0 ? void 0 : applicationSession.remountCreditCardForm(locale);
        }
        localeFirstRender.current = false;
    }, [locale]);
    return {
        creditCardPayload: creditCardPayload,
        selectedCardBrand: selectedCardBrand,
        showCardNumberContents: showCardNumberContents,
        cardNumberEmpty: cardNumberEmpty,
        showSecurityCodeContents: showSecurityCodeContents,
        securityCodeEmpty: securityCodeEmpty,
        showExpiryDateContents: showExpiryDateContents,
        expiryDateEmpty: expiryDateEmpty,
        supportedCardBrands: supportedCardBrands,
        cardValidityState: cardValidityState
    };
};
/**
 * React hook for creating the application session and returning the application session as a state object.
 * This should be called once per page / payment form or whenever the configuration changes.
 *
 * @param {BillingGatewayService} billingGatewayService - The billing gateway service used to create the application session.
 * @param {string | CreateApplicationSessionConfig} applicationSession -The application session id to use if available. CreateApplicationSessionConfig IS FOR INTERNAL USE ONLY. YOU SHOULD PASS IN APPLICATION SESSION ID YOU GENERATE! For devs: The configuration object for creating the application session.
 * @param {() => void} onPaymentSuccess - The callback to call when the payment is successful.
 * @param {() => void} onPaymentFailure - The callback to call when the payment fails.
 * @param {Locale} - currently supported values: see Internationalization/Locale
 * @returns {ApplicationSession | undefined} - The application session object or undefined if not available.
 */
export var useApplicationSession = function (billingGatewayService, applicationSession, onPaymentSuccess, onPaymentFailure, onProcessing, setErrorDetails, locale) {
    var _a = React.useState(undefined), applicationSessionResults = _a[0], setApplicationSessionResults = _a[1];
    useEffect(function () {
        if (typeof applicationSession === 'string') {
            billingGatewayService
                .getApplicationSession(applicationSession, onPaymentSuccess, onPaymentFailure, onProcessing, setErrorDetails, locale)
                .then(function (applicationSession) {
                setApplicationSessionResults(applicationSession);
            });
        }
        else {
            billingGatewayService
                .createApplicationSession(applicationSession, onPaymentSuccess, onPaymentFailure, onProcessing, setErrorDetails, locale)
                .then(function (applicationSession) {
                setApplicationSessionResults(applicationSession);
            });
        }
    }, []);
    return applicationSessionResults;
};
